import { useCallback, useRef, useState } from 'react';

import { useRequisicaoMaisRecente } from 'hooks/useRequisicaoMaisRecente';

import { findPlayer } from 'services/profile.service';
import { User } from 'types/user';

import { ColleagueSearch } from './recognition';

const EMPTY_SEARCH: ColleagueSearch = { searching: false, results: [], noResults: false, unknownId: false };

const STAFF_ID_PATTERN = /^F[0-9]+$/;

/** Busca de colega por nome ou matrícula, sem quem está buscando (REC-12 a REC-16, AJ3-38 a AJ3-41). */
export function useColleagueSearch(userId?: string) {
  const [search, setSearch] = useState<ColleagueSearch>(EMPTY_SEARCH);
  const { iniciar: startRequest, descartar: discardRequest } = useRequisicaoMaisRecente();
  // A escolha automática da busca confirmada (Enter) só vale enquanto a pessoa não volta a digitar (AJ3-40).
  const pickAllowed = useRef(false);

  /**
   * Com `pickOnlyMatch`, devolve a pessoa quando a busca acha uma só: ela já fica escolhida, sem lista (REC-13).
   * Na busca ao digitar ele vem falso, e a única pessoa fica na lista para ser escolhida (AJ3-39).
   */
  async function searchColleague(typed: string, pickOnlyMatch = true): Promise<User | undefined> {
    const term = typed.trim();

    if (!term || term === userId) {
      // Uma busca anterior ainda em andamento não pode trazer a lista depois deste termo.
      discardRequest();
      setSearch(EMPTY_SEARCH);
      return undefined;
    }

    const isLatest = startRequest();
    pickAllowed.current = pickOnlyMatch;
    // Os resultados da busca anterior ficam na tela até chegar a nova, para a lista não piscar a cada tecla.
    setSearch(current => ({ ...EMPTY_SEARCH, results: current.results, searching: true }));

    try {
      const found: User[] = await findPlayer(term);
      if (!isLatest()) return undefined;
      const others = found.filter(person => person._id !== userId);

      if (pickAllowed.current && others.length === 1) {
        setSearch(EMPTY_SEARCH);
        return others[0];
      }

      const noResults = others.length === 0;

      setSearch({ searching: false, results: others, noResults, unknownId: noResults && STAFF_ID_PATTERN.test(term) });
    } catch (error) {
      if (isLatest()) setSearch(EMPTY_SEARCH);
    }

    return undefined;
  }

  const clearSearch = useCallback(() => {
    discardRequest();
    setSearch(EMPTY_SEARCH);
  }, [discardRequest]);

  /** Digitar depois do Enter: a resposta que chegar fica na lista, sem escolher sozinha a única pessoa (AJ3-40). */
  const cancelAutoPick = useCallback(() => {
    pickAllowed.current = false;
  }, []);

  return { search, searchColleague, clearSearch, cancelAutoPick };
}
